import { Text } from '@chakra-ui/react'

import { MINIMUM_SAT_MATH_SCORE, MINIMUM_SAT_READING_SCORE } from '@/client/services/certification'
import { CAW_PROGRAM_DETAILS } from '@/client/services/collegeAdmissionsWorkshops/constants'
import { ALGEBRA2_LEARNING_LABS_PROGRAM_DETAILS } from '@/client/services/programs/ka-learning-labs/algebra2/constants'
import { PRE_CALCULUS_LEARNING_LABS_PROGRAM_DETAILS } from '@/client/services/programs/ka-learning-labs/pre-calculus/constants'
import { SAT_BOOTCAMP_PROGRAM_DETAILS } from '@/client/services/satBootcamp/constants'
import { getExploreUrl, getSettingsUrl } from '@/client/services/url'

import ShwLink from '@/components/ShwLink'

import {
  ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  COLLEGE_LOUNGE_WORLD_SLUG,
  JACKSON_WORLD_SLUG,
  PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
  SAT_BOOTCAMP_WORLD_SLUG,
  SAT_PREP_WORLD_SLUG,
  SCHOOLHOUSE_HUB_WORLD_SLUG,
  SCHOOLHOUSE_TUTORS_WORLD_SLUG,
  TORONTO_1_ON_1_WORLD_SLUG,
  TORONTO_GROUP_WORLD_SLUG,
} from './constants'
import type { CertificationOptionConfig, WorldConfig } from './types'

export const DEFAULT_WORLD_CONFIG: WorldConfig = {
  memberHomeType: 'default',
  nonMemberHomeType: 'default',
  certificationConfig: {
    options: [{ type: 'course-challenge' }, { type: 'units' }, { type: 'ap' }],
  },
  levelsToSkip: [],
  getNonMemberMessageForPrivateWorld: () => (
    <Text>You need an invitation to join this subworld.</Text>
  ),
  layout: 'community',
}

const SAT_CERTIFICATION_OPTION_CONFIGS: CertificationOptionConfig[] = [
  {
    type: 'units',
    title: 'Get certified in a single SAT Section',
    description:
      'If you qualify to be certified in either SAT Math *or* Reading & Writing, submit your SAT scores here.',
  },
  {
    type: 'course-challenge',
    title: 'Get certified in both SAT Math and Reading & Writing',
    description:
      'If you qualify to be certified in *both* SAT Math and Reading & Writing, submit your SAT scores here.',
  },
]

const SAT_GET_CERTIFIED_TEXT = `Schoolhouse allows you to verify your mastery for this subworld by submitting your SAT scores. If you meet our requirements of scoring ${MINIMUM_SAT_MATH_SCORE}+ in Math and/or ${MINIMUM_SAT_READING_SCORE}+ in Reading & Writing, you can help answer questions and host live sessions as a tutor.`

export const WORLD_SLUG_TO_CONFIG_OVERRIDE: Partial<Record<string, Partial<WorldConfig>>> = {
  [SAT_PREP_WORLD_SLUG]: {
    memberHomeType: 'default',
    nonMemberHomeType: 'default',
    certificationConfig: {
      options: SAT_CERTIFICATION_OPTION_CONFIGS,
      getCertifiedText: SAT_GET_CERTIFIED_TEXT,
      hideHowItWorks: true,
      hidePortfolioAlert: true,
    },
  },
  [SAT_BOOTCAMP_WORLD_SLUG]: {
    memberHomeType: 'programs',
    nonMemberHomeType: 'programs',
    certificationConfig: {
      options: SAT_CERTIFICATION_OPTION_CONFIGS,
      getCertifiedText: SAT_GET_CERTIFIED_TEXT,
      hideHowItWorks: true,
      hidePortfolioAlert: true,
    },
    layout: 'programs',
    programDescription: SAT_BOOTCAMP_PROGRAM_DETAILS.description,
  },
  [JACKSON_WORLD_SLUG]: {
    memberHomeType: 'default',
    nonMemberHomeType: 'default',
    certificationConfig: {
      options: [{ type: 'course-challenge' }],
    },
    applicationClosedReason: 'This program is currently not accepting tutor applications.',
  },
  [TORONTO_1_ON_1_WORLD_SLUG]: {
    memberHomeType: 'default',
    nonMemberHomeType: 'default',
    certificationConfig: {
      options: [{ type: 'course-challenge' }],
    },
    applicationClosedReason: 'This program is currently not accepting tutor applications.',
  },
  [TORONTO_GROUP_WORLD_SLUG]: {
    memberHomeType: 'default',
    nonMemberHomeType: 'default',
    certificationConfig: {
      options: [{ type: 'course-challenge' }],
    },
    applicationClosedReason: 'This program is currently not accepting tutor applications.',
  },
  [SCHOOLHOUSE_HUB_WORLD_SLUG]: {
    applicationClosedReason:
      'Only invited volunteers can become hosts in the Schoolhouse Hub subworld.',
  },
  [COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG]: {
    memberHomeType: 'programs',
    nonMemberHomeType: 'programs',
    layout: 'programs',
    seriesSort: 'programs',
    programDescription: CAW_PROGRAM_DETAILS.description,
  },
  [ALGEBRA2_LEARNING_LABS_WORLD_SLUG]: {
    memberHomeType: 'programs',
    nonMemberHomeType: 'programs',
    layout: 'programs',
    seriesSort: 'programs',
    programDescription: ALGEBRA2_LEARNING_LABS_PROGRAM_DETAILS.description,
    certificationConfig: { options: [{ type: 'course-challenge' }] },
  },
  [PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG]: {
    memberHomeType: 'programs',
    nonMemberHomeType: 'programs',
    layout: 'programs',
    seriesSort: 'programs',
    programDescription: PRE_CALCULUS_LEARNING_LABS_PROGRAM_DETAILS.description,
    certificationConfig: { options: [{ type: 'course-challenge' }] },
  },
  [COLLEGE_LOUNGE_WORLD_SLUG]: {
    levelsToSkip: ['member', 'senior-tutor', 'coach'],
    getNonMemberMessageForPrivateWorld: () => (
      <Text>
        If you would like to be automatically added to the College Lounge, please become a tutor in
        at least one subworld and verify your <b>current college enrollment</b>{' '}
        <ShwLink href={getSettingsUrl()}>from the settings page</ShwLink>.
      </Text>
    ),
  },
  [SCHOOLHOUSE_TUTORS_WORLD_SLUG]: {
    getNonMemberMessageForPrivateWorld: () => (
      <Text>
        If you would like to be automatically added to the Schoolhouse Tutors subworld, please{' '}
        <ShwLink href={getExploreUrl()}>become a tutor</ShwLink> in at least one subworld.
      </Text>
    ),
  },
}

export default function getWorldConfig(worldSlug: string): WorldConfig {
  return { ...DEFAULT_WORLD_CONFIG, ...WORLD_SLUG_TO_CONFIG_OVERRIDE[worldSlug] }
}
